import { DbEnum } from "../../../../generated/prisma-enums";

/**
 * The template kinds a workflow author can pick for `documentTemplates.create`
 * and `documentTemplates.list`, and the `DocumentTemplateType` each one means.
 *
 * **This object IS the contract.** The action catalog's `allowedValues` and
 * `allowedOptions` are derived from its keys, and `NexusApiNode` resolves a
 * user's choice through its values — so the set the node editor OFFERS and the
 * set the executor ACCEPTS cannot drift apart. They did: the executor carried a
 * private three-entry map and a `?? "WORD_TEMPLATE"` fallback, which minted a
 * Word template for any value outside it, and the list door cast the same three
 * strings straight to `DocumentTemplateType` — where `"WORD"` is not a member.
 *
 * The vocabulary here is deliberately NARROWER than `DocumentTemplateType`.
 * Those six members are the storage-level shapes the generation pipeline
 * distinguishes; these three are the choices a workflow author is shown. A kind
 * added here appears in the dropdown and is accepted by the executor in the same
 * change, and one that exists in only one of the two is not expressible.
 */
export const DOCUMENT_TEMPLATE_KIND_TO_TYPE = {
  WORD: DbEnum.DocumentTemplateType.WORD_TEMPLATE,
  POWERPOINT: DbEnum.DocumentTemplateType.POWERPOINT_TEMPLATE,
  HTML: DbEnum.DocumentTemplateType.WORD_CONTENT
} as const satisfies Record<string, DbEnum.DocumentTemplateType>;

/** A value a workflow author may supply as a template `type` / `filterType`. */
export type DocumentTemplateKind = keyof typeof DOCUMENT_TEMPLATE_KIND_TO_TYPE;

/**
 * The accepted kinds, in the order the node editor lists them.
 *
 * Declared as a literal tuple rather than derived with `Object.keys`, because its
 * ORDER is the order the node editor lists the kinds in, and `Object.keys`
 * returns `string[]` — which would need a cast and would lose that ordering
 * intent.
 *
 * ⚠️ `satisfies readonly DocumentTemplateKind[]` checks only that every value
 * listed here IS a key of the map. It does NOT check that every key is listed.
 * `_kindsAndMapAgree` below is what does, and it is not optional.
 */
export const DOCUMENT_TEMPLATE_KINDS = [
  "WORD",
  "POWERPOINT",
  "HTML"
] as const satisfies readonly DocumentTemplateKind[];

/**
 * A compile-time proof that the tuple above and the map are THE SAME SET.
 *
 * 🔴 THIS FILE'S HEADER USED TO CLAIM `satisfies` ALREADY DID THIS. It does not:
 * `satisfies readonly DocumentTemplateKind[]` checks that every listed value is
 * a key and says nothing about whether every key is listed. So dropping a kind
 * from the tuple, or adding one to the map and not to the tuple, compiled clean
 * — and the editor-offered set and the executor-accepted set drifted apart,
 * which is the exact failure this module exists to prevent. `isDocumentTemplateKind`
 * reads the MAP; the catalog's `allowedValues`, both dropdowns and the executor's
 * refusal message all read the TUPLE.
 *
 * Found in review. A comment asserting a guarantee the compiler does not give is
 * worse than no comment, because the next reader stops looking.
 *
 * BOTH directions are asserted, because they fail differently: a kind missing
 * from the tuple is accepted by the executor and never offered in the editor, and
 * a tuple entry that is not a key is offered and then refused.
 *
 * The tuple wrapper on each side is load-bearing — a bare `A extends B` would
 * distribute over the union and compare member-by-member, which is true for any
 * subset and would make this assertion vacuous in the direction that matters.
 */
type SameSet<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

const _kindsAndMapAgree: SameSet<DocumentTemplateKind, (typeof DOCUMENT_TEMPLATE_KINDS)[number]> =
  true;

/**
 * A runtime guard, not a cast — the value arrives as unvalidated workflow-node
 * configuration, so nothing upstream has checked it.
 *
 * `hasOwnProperty` rather than `in`, because `in` walks the prototype chain and
 * would accept `"toString"` and `"constructor"` as template kinds.
 */
export function isDocumentTemplateKind(value: string): value is DocumentTemplateKind {
  return Object.prototype.hasOwnProperty.call(DOCUMENT_TEMPLATE_KIND_TO_TYPE, value);
}
